import React, { useCallback, useState, createElement } from 'react';
import { Holding, LiquidityEvent, MacroShock } from '../types';
import { HoldingsForm } from '../components/HoldingsForm';
import { HoldingsList } from '../components/HoldingsList';
import { LiquidityEventForm } from '../components/LiquidityEventForm';
import { LiquidityEventList } from '../components/LiquidityEventList';
import { LoanForm } from '../components/LoanForm';
import { LoanList } from '../components/LoanList';
import { RealEstateForm } from '../components/RealEstateForm';
import { MacroShockForm } from '../components/MacroShockForm';
import { MacroShockList } from '../components/MacroShockList';
import { Wallet, CalendarClock, Landmark, Building2, Plus, X, Download, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Currency } from '../types';
function escapeCsvField(value: string | number | undefined | null): string {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}
function downloadCsv(filename: string, headers: string[], rows: (string | number | undefined | null)[][]) {
  const csv = [headers.map(escapeCsvField).join(','), ...rows.map(row => row.map(escapeCsvField).join(','))].join('\n');
  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
interface HoldingsPageProps {
  holdings: Holding[];
  liquidityEvents: LiquidityEvent[];
  loans?: any[];
  partnerNames: [string, string];
  baseCurrency?: Currency;
  baseCountry?: string;
  onAddHolding: (holding: Omit<Holding, 'id' | 'createdAt' | 'history'>) => void;
  onUpdateHoldingAmount: (id: string, newAmount: number) => void;
  onRenameHolding: (id: string, newName: string) => void;
  onUpdateCountry: (id: string, newCountry: string) => void;
  onUpdateHoldingFull?: (id: string, updates: Partial<Omit<Holding, 'id' | 'createdAt' | 'history'>>) => void;
  onToggleHoldingActive?: (id: string) => void;
  onDeleteHolding: (id: string) => void;
  onAddEvent: (event: Omit<LiquidityEvent, 'id'>) => string | void;
  onDeleteEvent: (id: string) => void;
  onUpdateEvent?: (id: string, updates: Partial<Omit<LiquidityEvent, 'id'>>) => void;
  onToggleEventActive?: (id: string) => void;
  onAddLoan?: (loan: any) => string | void;
  onDeleteLoan?: (id: string) => void;
  onUpdateLoan?: (id: string, updates: Partial<Omit<any, 'id' | 'createdAt'>>) => void;
  onToggleLoanActive?: (id: string) => void;
  macroShocks?: MacroShock[];
  onAddShock?: (shock: Omit<MacroShock, 'id'>) => void;
  onDeleteShock?: (id: string) => void;
  onUpdateShock?: (id: string, updates: Partial<Omit<MacroShock, 'id'>>) => void;
  onToggleShockActive?: (id: string) => void;
}
export function HoldingsPage({
  holdings,
  liquidityEvents,
  loans = [],
  partnerNames,
  baseCurrency = 'USD',
  baseCountry = 'US',
  onAddHolding,
  onUpdateHoldingAmount,
  onRenameHolding,
  onUpdateCountry,
  onUpdateHoldingFull = () => {},
  onToggleHoldingActive = () => {},
  onDeleteHolding,
  onAddEvent,
  onDeleteEvent,
  onUpdateEvent = () => {},
  onToggleEventActive = () => {},
  onAddLoan = () => {},
  onDeleteLoan = () => {},
  onUpdateLoan = () => {},
  onToggleLoanActive = () => {},
  macroShocks = [],
  onAddShock = () => {},
  onDeleteShock = () => {},
  onUpdateShock = () => {},
  onToggleShockActive = () => {}
}: HoldingsPageProps) {
  const [activeTab, setActiveTab] = useState<'holdings' | 'realestate' | 'events' | 'loans' | 'shocks'>('holdings');
  const [showForm, setShowForm] = useState(false);
  const [editingHoldingId, setEditingHoldingId] = useState<string | null>(null);
  const exportHoldings = useCallback(() => {
    const headers = ['Person', 'Name', 'Amount', 'Currency', 'Type', 'Country', 'Created', 'Is Real Estate', 'Purchase Price', 'Appreciation Rate %', 'Closing Costs', 'Rental Income (Monthly)', 'Linked Loan ID'];
    const rows = holdings.map(h => [h.person, h.name, h.amount, h.currency, h.isRealEstate ? 'Real Estate' : h.type, h.country, h.createdAt ? new Date(h.createdAt).toLocaleDateString() : '', h.isRealEstate ? 'Yes' : 'No', h.purchasePrice ?? '', h.appreciationRate ?? '', h.closingCosts ?? '', h.rentalIncome ?? '', h.linkedLoanId ?? '']);
    downloadCsv(`pairfolio-holdings-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  }, [holdings]);
  const exportEvents = useCallback(() => {
    const headers = ['Name', 'Amount', 'Currency', 'Frequency', 'Start Year', 'End Year'];
    const rows = liquidityEvents.map(e => [e.name, e.amount, e.currency, e.type, e.startYear, e.endYear ?? 'Perpetual']);
    downloadCsv(`pairfolio-cash-events-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  }, [liquidityEvents]);
  const exportLoans = useCallback(() => {
    const headers = ['Name', 'Owner', 'Total Amount', 'Deposit', 'Currency', 'Interest Rate', 'Duration (months)', 'Repayment Type', 'Start Year', 'Active'];
    const rows = loans.map((l: any) => [l.name, l.person, l.totalAmount, l.depositAmount, l.currency, `${l.interestRate}%`, l.durationMonths, l.repaymentType, l.startYear, l.isActive ? 'Yes' : 'No']);
    downloadCsv(`pairfolio-loans-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  }, [loans]);
  const exportShocks = useCallback(() => {
    const headers = ['Name', 'Year', 'Liquid Impact %', 'Illiquid Impact %', 'Real Estate Impact %', 'Active'];
    const rows = macroShocks.map(s => [s.name, s.year, `${s.liquidImpact}%`, `${s.illiquidImpact}%`, `${s.realEstateImpact}%`, s.isActive ?? true ? 'Yes' : 'No']);
    downloadCsv(`pairfolio-macro-shocks-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  }, [macroShocks]);
  const tabConfig = {
    holdings: {
      title: 'Holdings',
      subtitle: `${holdings.filter(h => !h.isRealEstate).length} asset${holdings.filter(h => !h.isRealEstate).length !== 1 ? 's' : ''} tracked`,
      addLabel: 'Add Holding',
      icon: Wallet,
      hasData: holdings.length > 0
    },
    realestate: {
      title: 'Real Estate',
      subtitle: `${holdings.filter(h => h.isRealEstate).length} propert${holdings.filter(h => h.isRealEstate).length !== 1 ? 'ies' : 'y'} tracked`,
      addLabel: 'Add Property',
      icon: Building2,
      hasData: holdings.filter(h => h.isRealEstate).length > 0
    },
    events: {
      title: 'Cash Events',
      subtitle: `${liquidityEvents.filter(e => e.isActive ?? true).length} active event${liquidityEvents.filter(e => e.isActive ?? true).length !== 1 ? 's' : ''} of ${liquidityEvents.length}`,
      addLabel: 'Add Event',
      icon: CalendarClock,
      hasData: liquidityEvents.length > 0
    },
    loans: {
      title: 'Loans & Debts',
      subtitle: `${loans.filter((l: any) => l.isActive).length} active loan${loans.filter((l: any) => l.isActive).length !== 1 ? 's' : ''}`,
      addLabel: 'Add Loan',
      icon: Landmark,
      hasData: loans.length > 0
    },
    shocks: {
      title: 'Stress Tests',
      subtitle: `${macroShocks.filter(s => s.isActive ?? true).length} active scenario${macroShocks.filter(s => s.isActive ?? true).length !== 1 ? 's' : ''} of ${macroShocks.length}`,
      addLabel: 'Add Scenario',
      icon: AlertTriangle,
      hasData: macroShocks.length > 0
    }
  };
  const current = tabConfig[activeTab];
  // Auto-close form when switching tabs
  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setShowForm(false);
  };
  // Wrap onAdd handlers to close form after successful add
  const handleAddHolding = (holding: Omit<Holding, 'id' | 'createdAt' | 'history'>) => {
    onAddHolding(holding);
    setShowForm(false);
  };
  const handleAddEvent = (event: Omit<LiquidityEvent, 'id'>) => {
    onAddEvent(event);
    setShowForm(false);
  };
  const handleAddLoan = (loan: any) => {
    onAddLoan(loan);
    setShowForm(false);
  };
  const handleAddShock = (shock: Omit<MacroShock, 'id'>) => {
    onAddShock(shock);
    setShowForm(false);
  };
  const handleEditHolding = (id: string, updates: Partial<Omit<Holding, 'id' | 'createdAt' | 'history'>>) => {
    onUpdateHoldingFull(id, updates);
    setEditingHoldingId(null);
  };
  const editingHolding = editingHoldingId ? holdings.find(h => h.id === editingHoldingId) : undefined;
  return <div className="flex flex-col md:flex-row gap-4 md:gap-6 min-h-[calc(100vh-8rem)]" data-id="element-1636">
      {/* Horizontal tabs on mobile, vertical sidebar on desktop */}
      <div className="md:w-48 md:shrink-0" data-id="element-1637">
        <div className="flex md:flex-col md:sticky md:top-20 gap-1 overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" data-id="element-1638">
          {(['holdings', 'realestate', 'events', 'loans', 'shocks'] as const).map(tab => {
          const config = tabConfig[tab];
          const Icon = config.icon;
          return <button key={tab} onClick={() => handleTabChange(tab)} className={`flex items-center gap-2 whitespace-nowrap py-2 px-3 text-sm font-medium rounded-lg transition-all text-left ${activeTab === tab ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`} data-id="element-1639">
                <Icon className="w-4 h-4 shrink-0" data-id="element-1640" />
                {config.title}
              </button>;
        })}
        </div>
      </div>

      {/* Content Area — full width */}
      <div className="flex-1 min-w-0 space-y-5" data-id="element-1641">
        {/* Content Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1642">
          <div data-id="element-1643">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-1644">
              {current.title}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1645">{current.subtitle}</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-1646">
            {current.hasData && <Button variant="secondary" size="md" onClick={() => {
            if (activeTab === 'holdings' || activeTab === 'realestate') exportHoldings();else if (activeTab === 'events') exportEvents();else if (activeTab === 'loans') exportLoans();else if (activeTab === 'shocks') exportShocks();
          }} data-id="element-1647">
                <Download className="w-4 h-4 mr-1.5" data-id="element-1648" />
                Export
              </Button>}
            <Button variant={showForm ? 'secondary' : 'primary'} size="md" onClick={() => setShowForm(!showForm)} data-id="element-1649">
              {showForm ? <>
                  <X className="w-4 h-4 mr-1.5" data-id="element-1650" />
                  Cancel
                </> : <>
                  <Plus className="w-4 h-4 mr-1.5" data-id="element-1651" />
                  {current.addLabel}
                </>}
            </Button>
          </div>
        </div>

        {/* Collapsible Form Panel */}
        {showForm && <div className="animate-in slide-in-from-top-2 fade-in duration-200" data-id="element-1652">
            {activeTab === 'holdings' && <HoldingsForm onAdd={handleAddHolding} partnerNames={partnerNames} baseCountry={baseCountry} baseCurrency={baseCurrency} data-id="element-1653" />}
            {activeTab === 'realestate' && <RealEstateForm onAdd={handleAddHolding} partnerNames={partnerNames} loans={loans} liquidityEvents={liquidityEvents} holdings={holdings} onAddLoan={onAddLoan} onAddEvent={onAddEvent} baseCurrency={baseCurrency} baseCountry={baseCountry} data-id="element-1654" />}
            {activeTab === 'events' && <LiquidityEventForm onAdd={handleAddEvent} baseCurrency={baseCurrency} data-id="element-1655" />}
            {activeTab === 'loans' && <LoanForm onAdd={handleAddLoan} partnerNames={partnerNames} holdings={holdings} baseCurrency={baseCurrency} data-id="element-1656" />}
            {activeTab === 'shocks' && <MacroShockForm onAdd={handleAddShock} data-id="element-1657" />}
          </div>}

        {/* Full-width List */}
        <div className="animate-in fade-in duration-300" data-id="element-1658">
          {(activeTab === 'holdings' || activeTab === 'realestate') && <HoldingsList holdings={activeTab === 'realestate' ? holdings.filter(h => h.isRealEstate) : holdings.filter(h => !h.isRealEstate)} variant={activeTab === 'realestate' ? 'realestate' : 'holdings'} onDelete={onDeleteHolding} onUpdateAmount={onUpdateHoldingAmount} onRename={onRenameHolding} onUpdateCountry={onUpdateCountry} partnerNames={partnerNames} onExport={exportHoldings} onEditHolding={id => setEditingHoldingId(id)} onToggleActive={onToggleHoldingActive} data-id="element-1659" />}
          {activeTab === 'events' && <LiquidityEventList events={liquidityEvents} onDelete={onDeleteEvent} onUpdate={onUpdateEvent} onToggleActive={onToggleEventActive} onExport={exportEvents} data-id="element-1660" />}
          {activeTab === 'loans' && <LoanList loans={loans} partnerNames={partnerNames} onDelete={onDeleteLoan} onUpdate={onUpdateLoan} onToggleActive={onToggleLoanActive} onExport={exportLoans} data-id="element-1661" />}
          {activeTab === 'shocks' && <MacroShockList shocks={macroShocks} onDelete={onDeleteShock} onUpdate={onUpdateShock} onToggleActive={onToggleShockActive} data-id="element-1662" />}
        </div>
      </div>

      {/* Edit Real Estate Modal */}
      {editingHolding && editingHolding.isRealEstate && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto" data-id="element-1663">
          <div className="relative w-full max-w-2xl max-h-full my-8" data-id="element-1664">
            <RealEstateForm onAdd={handleAddHolding} partnerNames={partnerNames} loans={loans} liquidityEvents={liquidityEvents} holdings={holdings} onAddLoan={onAddLoan} onAddEvent={onAddEvent} baseCurrency={baseCurrency} baseCountry={baseCountry} editingHolding={editingHolding} onEdit={handleEditHolding} onCancelEdit={() => setEditingHoldingId(null)} data-id="element-1665" />
          </div>
        </div>}

      {/* Edit Non-RE Holding Modal */}
      {editingHolding && !editingHolding.isRealEstate && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto" data-id="element-1666">
          <div className="relative w-full max-w-2xl max-h-full my-8" data-id="element-1667">
            <HoldingsForm onAdd={handleAddHolding} partnerNames={partnerNames} baseCountry={baseCountry} baseCurrency={baseCurrency} editingHolding={editingHolding} onEdit={handleEditHolding} onCancelEdit={() => setEditingHoldingId(null)} data-id="element-1668" />
          </div>
        </div>}
    </div>;
}